import { signal, derived } from './signals.js';
import { fetchCocktails } from './fetchCocktails.js';
import { filterCocktails } from './filterCocktails.js';
import { Favorites } from './favorites.js';

import { Validator, ValidationHelpers } from './validation.js';
// Temporarily remove config import to prevent circular dependency
// import config from './config.js';

// Hard-code config values to avoid circular dependency
const config = {
  API_ENDPOINT: 'https://sheets.livepolls.app/api/spreadsheets/9664b907-bcb0-4e3a-8394-1d0b4adaba5e/Sheet1',
  SHOW_STATE_IN_CONSOLE: false // Disable in production to avoid circular dependency
};

// Centralized function to get cocktail ID from feed data with validation
export function getCocktailId(cocktail) {
  return ValidationHelpers.safeCocktailId(cocktail);
}

// Use global logger to avoid circular dependencies
const log = typeof window !== 'undefined' && window.Log ? window.Log('cocktail-state') : {
  blue: () => {}, red: () => {}, green: () => {}, yellow: () => {}, purple: () => {}, log: () => {}, table: () => {}
};

// Core state signals
export const cocktails = signal([]);
export const selectedCocktail = signal(null);
export const searchTerm = signal('');
export const loading = signal(false);
export const error = signal(null);
export const favorites = signal(new Set());
export const showFavoritesOnly = signal(false);

// Initialize favorites from localStorage
favorites.value = Favorites.getFavorites();

// Derived state - NOTE: Both search and favorites filtering now handled by CSS
// This derived signal now just returns the raw cocktails for initial rendering
// All filtering is done via CSS show/hide for maximum performance
export const filteredCocktails = derived(() => {
  // Return all cocktails - filtering now handled by CSS in cocktail-list component
  // This avoids expensive array operations and DOM re-rendering
  return cocktails.value;
});

export const hasSelection = derived(() => {
  return selectedCocktail.value !== null;
});

export const cocktailCount = derived(() => {
  return cocktails.value.length;
});

// Changed from derived to signal so it can be updated by the cocktail-list component
export const filteredCount = signal(0);

export const favoritesCount = derived(() => {
  return favorites.value.size;
});

// Actions (functions that update state)
export async function loadCocktails() {
  log.green('Loading cocktails from:', config.API_ENDPOINT);
  loading.value = true;
  error.value = null;
  
  try {
    const data = await fetchCocktails(config.API_ENDPOINT);
    
    // Validate all cocktails before processing
    const validatedData = Validator.validateCocktails(data);
    
    // Sort cocktails alphabetically by name
    const sortedData = validatedData.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
    
    cocktails.value = sortedData;
    log.green('Cocktails loaded and validated:', sortedData.length);
    
    // Dispatch event for router to update cocktails map
    document.dispatchEvent(new CustomEvent('cocktails-loaded', {
      detail: { cocktails: sortedData }
    }));
  } catch (err) {
    log.red('Failed to load cocktails:', err);
    error.value = err.message;
  } finally {
    loading.value = false;
  }
}

export function selectCocktail(cocktail) {
  log.green('Selecting cocktail:', cocktail?.name);
  
  // Validate cocktail before setting
  if (cocktail) {
    const validatedCocktail = ValidationHelpers.safeCocktail(cocktail);
    if (!validatedCocktail) {
      log.yellow('Invalid cocktail data, selection cancelled');
      error.value = 'Selected cocktail data is invalid';
      return;
    }
    selectedCocktail.value = validatedCocktail;
  } else {
    selectedCocktail.value = null;
  }
}

export function updateSearch(term) {
  // Validate and sanitize search term
  const validatedTerm = Validator.validateSearchTerm(term);
  log.green('Updating search term:', validatedTerm);
  searchTerm.value = validatedTerm;
  
  // Clear selection when searching to show empty state
  if (validatedTerm.trim()) {
    selectedCocktail.value = null;
  }
}

export function clearSearch() {
  log.green('Clearing search');
  searchTerm.value = '';
}

export function clearSelection() {
  log.green('Clearing selection');
  selectedCocktail.value = null;
}

// Favorites actions
export function toggleFavorite(cocktailId) {
  log.blue('State toggleFavorite called with:', cocktailId);
  
  // Validate cocktail ID
  try {
    const validatedId = Validator.validateCocktailId(cocktailId);
    
    const isFavorited = Favorites.toggleFavorite(validatedId);
    log.blue('Favorites.toggleFavorite returned:', isFavorited);
    
    // Update the signal to trigger reactive updates
    favorites.value = Favorites.getFavorites();
    log.blue('Updated favorites signal:', favorites.value);
    
    log.green('Toggled favorite:', validatedId, 'Now favorited:', isFavorited);
    return isFavorited;
  } catch (error) {
    log.red('Invalid cocktail ID for favorite toggle:', error.message);
    return false;
  }
}

export function isFavorite(cocktailId) {
  try {
    const validatedId = Validator.validateCocktailId(cocktailId);
    return favorites.value.has(validatedId);
  } catch (error) {
    log.yellow('Invalid cocktail ID for favorite check:', error.message);
    return false;
  }
}

export function toggleFavoritesFilter() {
  log.green('Toggling favorites filter:', !showFavoritesOnly.value);
  showFavoritesOnly.value = !showFavoritesOnly.value;
  
  // Clear selection when toggling filter to show empty state
  selectedCocktail.value = null;
  
  return showFavoritesOnly.value;
}

// Action to update filtered count from cocktail-list component
export function updateFilteredCount(count) {
  filteredCount.value = count;
  log.blue('Updated filtered count:', count);
}

// Debug helpers
export function getState() {
  return {
    cocktails: cocktails.value,
    selected: selectedCocktail.value,
    searchTerm: searchTerm.value,
    loading: loading.value,
    error: error.value,
    filteredCount: filteredCount.value,
    totalCount: cocktailCount.value
  };
}

// Make state available globally for debugging (development only)
if (config.SHOW_STATE_IN_CONSOLE && typeof window !== 'undefined') {
  window.cocktailState = {
    getState,
    selectCocktail,
    updateSearch,
    clearSearch,
    clearSelection,
    loadCocktails
  };
  log.blue('State exposed to window.cocktailState for debugging');
}
